import { Component } from '@theme/component';
import { prefersReducedMotion, clamp } from '@theme/utilities';

/**
 * Scroll-driven video scrubbing with an optional sticky stage and scroll-synced
 * annotation reveals.
 *
 * The section renders a tall `track` containing a `stage`. As the visitor scrolls
 * through the track, scroll progress (0 → 1) is mapped onto:
 *   - the underlying `<video>` currentTime (when scrubbing is enabled), and
 *   - each annotation's `[reveal-start, reveal-end]` window.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [track] - The tall scroll track.
 * @property {HTMLElement} [stage] - The (optionally sticky) stage holding the video + annotations.
 * @property {HTMLElement[]} [annotation] - The annotation blocks (ref="annotation[]").
 *
 * @extends {Component<Refs>}
 */
class ScrollScrubVideo extends Component {
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {number} */
  #duration = 0;
  /** @type {number} */
  #targetTime = 0;
  /** @type {number} */
  #currentTime = 0;
  /** @type {number} */
  #progress = -1;
  /** @type {number | null} */
  #rafId = null;
  /** @type {boolean} */
  #running = false;
  /** @type {boolean} */
  #reduced = false;
  /** @type {AbortController} */
  #abort = new AbortController();
  /** @type {AbortController | null} */
  #videoBindAbort = null;
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {MutationObserver | null} */
  #videoWatcher = null;
  /** @type {boolean | null} Tracks the last viewport breakpoint used to pick a video. */
  #isDesktop = null;
  /** @type {IntersectionObserver | null} Reveal-on-arrival observer (no-scrubbing mode). */
  #revealIo = null;
  /** @type {boolean} Whether the scroll-track listeners are bound. */
  #trackBound = false;
  /** @type {boolean} Whether the breakpoint resize listener is bound. */
  #breakpointBound = false;
  /** @type {number[]} Pending staggered-reveal timeout ids, cleared when hiding. */
  #revealTimers = [];
  /** @type {number | null} Trigger offset the arrival observer was built with. */
  #revealOffset = null;
  /** @type {boolean} Whether the video is frozen (paused/held) because no playback mode is active. */
  #frozen = false;
  /** @type {AbortController | null} Holds the "keep paused" listeners while frozen. */
  #freezeAbort = null;

  /** Whether a scroll track (sticky and/or scrubbing) is present. */
  get hasTrack() {
    return this.dataset.hasTrack === 'true';
  }

  /** Whether a separate mobile video is provided (desktop/mobile switch). */
  get hasMobileVideo() {
    return this.dataset.hasMobileVideo === 'true';
  }

  /** Whether the video should play when the section appears (and pause when it leaves). */
  get playOnAppearance() {
    return this.dataset.appearance === 'true';
  }

  /** Replay (restart video + re-animate annotations) on every re-entry. */
  get replayOnReenter() {
    return this.dataset.replay === 'true';
  }

  /**
   * Whether annotations animate in when the section arrives in view. Explicit opt-in,
   * works in both modes:
   *  - with scrubbing → annotations animate on arrival instead of following the scroll;
   *  - on appearance  → annotations animate in (otherwise they're shown statically).
   */
  get animateAnnotations() {
    return this.dataset.revealOnArrival === 'true';
  }

  /** Annotations driven by scroll progress ([reveal-start, reveal-end]) — scrubbing only. */
  get scrollSyncedAnnotations() {
    return this.scrubbing && !this.animateAnnotations;
  }

  /**
   * How far into the viewport (from the bottom) the section must travel before the
   * arrival animation fires, as a percentage. 0 = as soon as it touches the bottom
   * edge; higher = it must rise further up the screen first.
   */
  get revealTriggerOffset() {
    const value = parseFloat(this.dataset.revealTrigger ?? '10');
    return clamp(isNaN(value) ? 10 : value, 0, 100);
  }

  /** Whether the video currentTime should follow scroll progress. */
  get scrubbing() {
    return this.dataset.scrubbing === 'true';
  }

  /** Sticky offset in px (e.g. to clear a sticky header), per breakpoint. */
  get stickyOffset() {
    const value = window.innerWidth >= 750 ? this.dataset.stickyOffset : this.dataset.stickyOffsetMobile;
    return parseFloat(value ?? '0') || 0;
  }

  /** Smoothing amount, 0 (instant) → 1 (very smooth). */
  get smoothing() {
    const value = parseFloat(this.dataset.smoothing ?? '0.3');
    return clamp(isNaN(value) ? 0.3 : value, 0, 1);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#reduced = prefersReducedMotion();
    const { signal } = this.#abort;

    this.#syncMode();

    // When the intro is pinned inside the stage, expose its height so the video
    // can shrink to sit below it (no overlap).
    if (this.classList.contains('scroll-scrub--sticky-intro')) {
      requestAnimationFrame(this.#measureIntro);
      window.addEventListener('resize', this.#measureIntro, { passive: true, signal });
    }

    // Theme editor: surface the annotation the merchant is editing.
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
  }

  /**
   * The theme editor morphs the section in place rather than recreating it, so
   * connectedCallback does NOT re-run when a setting (e.g. scrubbing) is toggled.
   * Re-sync the reveal mode here so annotations switch behaviour live.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncMode();
  }

  /**
   * Idempotently wires up the behaviour for the current settings. Safe to call on
   * connect AND on every editor re-render (morph). Each piece guards against
   * double-binding so toggling scrubbing/sticky on/off keeps working.
   */
  #syncMode() {
    const { signal } = this.#abort;

    // --- Video timeline ---
    if (this.scrubbing) {
      // Take ownership of the scrubbed video once scrubbing becomes active.
      if (!this.#video) {
        this.#isDesktop = window.innerWidth >= 750;
        this.#acquireVideo();
        // Re-bind to the matching video when crossing the 750px breakpoint.
        if (this.hasMobileVideo && !this.#breakpointBound) {
          this.#breakpointBound = true;
          window.addEventListener('resize', this.#onBreakpointChange, { passive: true, signal });
        }
      }
    } else {
      // Release the scrubbed video (drop the "hold paused" listener) so it can be
      // played natively on arrival; re-acquired if scrubbing is turned back on.
      this.#videoBindAbort?.abort();
      this.#videoBindAbort = null;
      this.#video = null;
    }

    // No playback mode at all → keep the video paused on its first frame (the markup
    // renders a native `autoplay` video, so we must actively hold it). Otherwise let
    // scrubbing or the arrival observer drive it.
    if (this.scrubbing || this.playOnAppearance) {
      this.#unfreezeVideos();
    } else {
      this.#freezeVideos();
    }

    // Bind the scroll track once (sticky and/or scrubbing).
    if (this.hasTrack && !this.#trackBound) {
      this.#trackBound = true;
      window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      window.addEventListener('resize', this.#onScroll, { passive: true, signal });
      this.#observeViewport();
    }

    // --- Arrival observer ---
    // Needed to (a) play/pause the video on appearance, and/or (b) animate annotations
    // in on arrival. Not needed when the video scrubs and annotations are scroll-synced.
    const needArrival = this.animateAnnotations || (this.playOnAppearance && !this.scrubbing);
    if (needArrival) {
      // Rebuild the observer if absent or if the trigger offset changed (editor tweak),
      // since rootMargin is fixed at construction time.
      if (!this.#revealIo || this.#revealOffset !== this.revealTriggerOffset) {
        this.#revealIo?.disconnect();
        this.#revealIo = null;
        this.#revealOffset = this.revealTriggerOffset;
        this.#revealOnArrival();
      }
    } else {
      this.#revealIo?.disconnect();
      this.#revealIo = null;
    }

    // Paint the initial scrub frame / scroll-window state so nothing flashes in.
    if (this.scrubbing) this.#tick(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#videoBindAbort?.abort();
    this.#freezeAbort?.abort();
    this.#stopLoop();
    this.#clearRevealTimers();
    this.#io?.disconnect();
    this.#revealIo?.disconnect();
    this.#videoWatcher?.disconnect();
  }

  /**
   * The DOM subtree holding the video that matches the current viewport.
   * Falls back to the whole component when there's no desktop/mobile split.
   * @returns {ParentNode}
   */
  #videoScope() {
    if (!this.hasMobileVideo) return this;
    const selector = this.#isDesktop ? '[data-video-desktop]' : '[data-video-mobile]';
    return this.querySelector(selector) ?? this;
  }

  /**
   * Grabs the `<video>` element rendered by the `video` snippet for the active
   * breakpoint. The element may not be in the DOM yet (deferred-media), so fall
   * back to a one-shot observer.
   */
  #acquireVideo() {
    const scope = this.#videoScope();
    const video = scope.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      this.#bindVideo(video);
      return;
    }

    this.#videoWatcher?.disconnect();
    this.#videoWatcher = new MutationObserver(() => {
      const candidate = this.#videoScope().querySelector('video');
      if (candidate instanceof HTMLVideoElement) {
        this.#videoWatcher?.disconnect();
        this.#videoWatcher = null;
        this.#bindVideo(candidate);
      }
    });
    this.#videoWatcher.observe(this, { childList: true, subtree: true });
  }

  /** Re-bind to the breakpoint-matching video when the 750px line is crossed. */
  #onBreakpointChange = () => {
    const isDesktop = window.innerWidth >= 750;
    if (isDesktop === this.#isDesktop) return;
    this.#isDesktop = isDesktop;

    // Pause and rewind the now-hidden video so it doesn't keep buffering/playing.
    if (this.#video) {
      try {
        this.#video.pause();
      } catch {
        // no-op
      }
    }

    this.#duration = 0;
    this.#currentTime = 0;
    this.#targetTime = 0;
    this.#acquireVideo();
    this.#tick(true);
  };

  /**
   * Takes ownership of the video: stops native playback and prepares it for
   * frame seeking driven by scroll.
   *
   * @param {HTMLVideoElement} video
   */
  #bindVideo(video) {
    this.#video = video;
    // Per-bind controller so listeners from a previous video are dropped on re-bind.
    this.#videoBindAbort?.abort();
    this.#videoBindAbort = new AbortController();
    const { signal } = this.#videoBindAbort;

    video.removeAttribute('loop');
    video.removeAttribute('autoplay');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    // We own the timeline — never let it auto-play.
    const hold = () => {
      if (!video.paused) video.pause();
    };
    video.addEventListener('play', hold, { signal });
    hold();

    const onMeta = () => {
      this.#duration = video.duration || 0;
      this.#tick(true);
    };

    if (video.readyState >= 1 && video.duration) {
      onMeta();
    } else {
      video.addEventListener('loadedmetadata', onMeta, { signal });
    }

    // Nudge the browser into buffering so seeks feel responsive.
    try {
      video.load();
    } catch {
      // no-op
    }
  }

  #onScroll = () => {
    if (!this.#running) this.#startLoop();
  };

  /** Run the rAF loop only while the section is near the viewport. */
  #observeViewport() {
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#startLoop();
          } else {
            this.#stopLoop();
            this.#tick(true);
          }
        }
      },
      { rootMargin: '120px 0px' }
    );
    this.#io.observe(this);
  }

  #startLoop() {
    if (this.#running) return;
    this.#running = true;

    const loop = () => {
      if (!this.#running) return;
      this.#tick(false);
      this.#rafId = requestAnimationFrame(loop);
    };

    this.#rafId = requestAnimationFrame(loop);
  }

  #stopLoop() {
    this.#running = false;
    if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
  }

  /**
   * Scroll progress (0 → 1) through the track while the stage is pinned.
   * @returns {number}
   */
  #computeProgress() {
    const { track, stage } = this.refs;
    if (!track) return 0;

    const rect = track.getBoundingClientRect();
    const stageHeight = stage ? stage.offsetHeight : window.innerHeight;
    const distance = track.offsetHeight - stageHeight;
    if (distance <= 0) return 0;

    const scrolled = clamp(this.stickyOffset - rect.top, 0, distance);
    return scrolled / distance;
  }

  /**
   * @param {boolean} force - Recompute reveals/target even if progress is unchanged.
   */
  #tick(force) {
    const progress = this.#computeProgress();

    if (force || Math.abs(progress - this.#progress) > 0.0001) {
      this.#progress = progress;
      this.style.setProperty('--progress', progress.toFixed(4));
      // Scroll-synced reveal only in that mode; arrival animation is handled by #revealOnArrival.
      if (this.scrollSyncedAnnotations) this.#updateAnnotations(progress);
      this.#targetTime = progress * this.#duration;
    }

    if (this.scrubbing && this.#video && this.#duration > 0) {
      this.#applyScrub();
    }
  }

  /** Eases the video currentTime toward the scroll target. */
  #applyScrub() {
    const video = this.#video;
    if (!video) return;

    const factor = this.#reduced ? 1 : clamp(1 - this.smoothing * 0.92, 0.06, 1);
    this.#currentTime += (this.#targetTime - this.#currentTime) * factor;

    if (Math.abs(this.#targetTime - this.#currentTime) < 0.008) {
      this.#currentTime = this.#targetTime;
    }

    if (Math.abs(video.currentTime - this.#currentTime) > 0.01 && !video.seeking) {
      try {
        video.currentTime = this.#currentTime;
      } catch {
        // Seeking can throw before the media is seekable — ignore.
      }
    }
  }

  /** @returns {HTMLElement[]} */
  #annotations() {
    const ref = this.refs.annotation;
    if (!ref) return [];
    return Array.isArray(ref) ? ref : [ref];
  }

  /**
   * Toggle annotation visibility based on their scroll window.
   * @param {number} progress
   */
  #updateAnnotations(progress) {
    for (const annotation of this.#annotations()) {
      if (annotation.dataset.forceVisible === 'true') continue;

      const start = parseFloat(annotation.dataset.revealStart || '0');
      const end = parseFloat(annotation.dataset.revealEnd || '1');
      annotation.classList.toggle('is-visible', progress >= start && progress <= end);
    }
  }

  /**
   * No-scrubbing mode: when the section comes into view, start the video playing AND
   * reveal all annotations ("apparition à l'arrivée"), staggered by each block's delay.
   * The video is paused again when the section scrolls out of view. Annotations only
   * reveal once.
   *
   * We observe a reliably-sized element (the stage, fallback to the component) rather
   * than each annotation's tiny content box — the per-box approach was fragile (0-area
   * anchors, morph-detached nodes) and could fail to fire.
   */
  #revealOnArrival() {
    const annotations = this.#annotations();
    const target = this.refs.stage ?? this;
    let revealed = false;

    // Drive the video only in appearance mode and only when scrubbing isn't already
    // driving its timeline.
    const controlsVideo = () => this.playOnAppearance && !this.scrubbing;

    const onEnter = () => {
      if (controlsVideo()) this.#playVideos(this.replayOnReenter && !revealed);
      if (this.animateAnnotations && !revealed) {
        revealed = true;
        this.#revealAll(annotations);
      }
    };

    const onLeave = () => {
      if (controlsVideo()) this.#pauseVideos();
      // "Replay" mode: reset so the next entry re-animates the annotations from scratch.
      if (this.replayOnReenter && revealed && this.animateAnnotations) {
        revealed = false;
        this.#hideAll(annotations);
      }
    };

    const offset = this.revealTriggerOffset;
    this.#revealIo = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onEnter();
        } else {
          onLeave();
        }
      },
      { threshold: 0, rootMargin: `0px 0px -${offset}% 0px` }
    );
    this.#revealIo.observe(target);

    // Already past the trigger line at setup (e.g. above the fold, or toggled in the
    // editor while on screen)? Trigger immediately — don't wait for a scroll event.
    const rect = target.getBoundingClientRect();
    const triggerLine = window.innerHeight * (1 - offset / 100);
    if (rect.top < triggerLine && rect.bottom > 0) onEnter();
  }

  /**
   * Play every on-screen video in the section (muted, inline). No-op for hidden ones.
   * @param {boolean} [restart] - Rewind to the start before playing.
   */
  #playVideos(restart = false) {
    for (const video of this.querySelectorAll('video')) {
      // Skip the breakpoint-hidden video (display:none → no offsetParent).
      if (!(video instanceof HTMLVideoElement) || video.offsetParent === null) continue;
      video.muted = true;
      video.playsInline = true;
      if (restart) {
        try {
          video.currentTime = 0;
        } catch {
          // currentTime can throw before the media is seekable — ignore.
        }
      }
      const played = video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    }
  }

  /**
   * Hold every video paused on its first frame (no playback mode active). The markup
   * renders a native `autoplay` video, so we pause it and keep it paused on any `play`.
   */
  #freezeVideos() {
    if (this.#frozen) return;
    this.#frozen = true;
    this.#freezeAbort = new AbortController();
    const { signal } = this.#freezeAbort;
    for (const video of this.querySelectorAll('video')) {
      if (!(video instanceof HTMLVideoElement)) continue;
      const hold = () => {
        if (!video.paused) video.pause();
      };
      video.addEventListener('play', hold, { signal });
      try {
        video.pause();
        video.currentTime = 0;
      } catch {
        // no-op
      }
    }
  }

  /** Release the freeze "hold paused" listeners so the video can play again. */
  #unfreezeVideos() {
    if (!this.#frozen) return;
    this.#frozen = false;
    this.#freezeAbort?.abort();
    this.#freezeAbort = null;
  }

  /** Pause every video in the section. */
  #pauseVideos() {
    for (const video of this.querySelectorAll('video')) {
      if (video instanceof HTMLVideoElement && !video.paused) {
        try {
          video.pause();
        } catch {
          // no-op
        }
      }
    }
  }

  /**
   * Add `is-visible` to every annotation, staggered by its `data-reveal-delay` (ms).
   * @param {HTMLElement[]} annotations
   */
  #revealAll(annotations) {
    this.#clearRevealTimers();
    for (const annotation of annotations) {
      const delay = parseFloat(annotation.dataset.revealDelay || '0') || 0;
      if (delay > 0 && !this.#reduced) {
        this.#revealTimers.push(setTimeout(() => annotation.classList.add('is-visible'), delay));
      } else {
        annotation.classList.add('is-visible');
      }
    }
  }

  /**
   * Remove `is-visible` from every annotation so they re-animate on the next entry.
   * @param {HTMLElement[]} annotations
   */
  #hideAll(annotations) {
    this.#clearRevealTimers();
    for (const annotation of annotations) {
      annotation.classList.remove('is-visible');
    }
  }

  /** Cancel any pending staggered-reveal timeouts. */
  #clearRevealTimers() {
    for (const id of this.#revealTimers) clearTimeout(id);
    this.#revealTimers = [];
  }

  /** Expose the pinned intro height as --intro-height so the video can fit below it. */
  #measureIntro = () => {
    const introWrap = this.querySelector('.scroll-scrub__intro-wrap');
    if (introWrap instanceof HTMLElement) {
      this.style.setProperty('--intro-height', `${introWrap.offsetHeight}px`);
    }
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && this.contains(target) && target.classList.contains('scroll-scrub__annotation')) {
      target.dataset.forceVisible = 'true';
      target.classList.add('is-visible');
    }
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && this.contains(target) && target.classList.contains('scroll-scrub__annotation')) {
      delete target.dataset.forceVisible;
      this.#tick(true);
    }
  };
}

if (!customElements.get('scroll-scrub-video')) {
  customElements.define('scroll-scrub-video', ScrollScrubVideo);
}
